import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { useTranslation } from 'react-i18next';
import type { AwpDocument } from './types';

interface AwpDocumentRowProps {
  document: AwpDocument;
  pending: boolean;
  onDownload: (doc: AwpDocument) => void;
}

/**
 * One visit's document: a file tile, "{year} AWP", its exam date, and a download button. A member can
 * have several visits in one year, so the exam date is what tells rows apart, and the button's
 * accessible name carries it in full.
 */
export function AwpDocumentRow({ document, pending, onDownload }: AwpDocumentRowProps) {
  const { t } = useTranslation('shared');
  const { formatDateMedium } = useFormatDate();
  const date = formatDateMedium(document.examDate);

  return (
    <li className="flex items-center gap-3 rounded-lg border p-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
        <Icon name="filePdf" />
      </span>
      <div className="min-w-0 flex-1">
        <span className="text-sm font-semibold text-foreground">
          {t('awpDocuments.rowTitle', { year: document.examDate.slice(0, 4) })}
        </span>
        <dl className="m-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <dt className="font-semibold">{t('awpDocuments.examDate')}</dt>
          <dd className="m-0">
            <time dateTime={document.examDate}>{date}</time>
          </dd>
        </dl>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-11 shrink-0"
        title={t('awpDocuments.download')}
        aria-label={t('awpDocuments.downloadLabel', { date })}
        aria-busy={pending}
        disabled={pending}
        onClick={() => onDownload(document)}
      >
        {pending ? (
          <Icon name="spinner" weight="solid" size="sm" className="animate-spin" />
        ) : (
          <Icon name="download" size="sm" />
        )}
      </Button>
    </li>
  );
}
